import { useState, useRef, useEffect } from 'react'
import {
  Box,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Button,
  IconButton,
  Paper,
  Container,
  useTheme,
  useMediaQuery,
  styled
} from '@mui/material'
import topImage from '../assets/top.png'
import {
  ArrowBackIos,
  ArrowForwardIos,
  Phone,
  Email
} from '@mui/icons-material'
import { useContentByEmbed } from '../hooks/useContentByEmbed'

// Styled Components for Widget Isolation
const WidgetContainer = styled(Box)(() => ({
  '& *': {
    boxSizing: 'border-box',
  },
  '& .widget-scoped': {
    fontFamily: "'Montserrat', sans-serif",
  },
  '& .widget-scoped h1, & .widget-scoped h2, & .widget-scoped h3, & .widget-scoped h4, & .widget-scoped h5, & .widget-scoped h6': {
    fontFamily: "'Montserrat', sans-serif",
    margin: 0,
  },
  '& .widget-scoped p': {
    fontFamily: "'Montserrat', sans-serif",
    margin: 0,
  },
  '& .widget-scoped button': {
    fontFamily: "'Montserrat', sans-serif",
  },
  '& .widget-scoped input, & .widget-scoped textarea, & .widget-scoped select': {
    fontFamily: "'Montserrat', sans-serif",
  },
}))


// Agent Card Component
interface AgentCardProps {
  agent: {
    id: number
    data: {
      fullName: string
      bio: string
      photo: string
      email: string
      phone: string
      whatsapp: string
      rating: number
      reviewsCount: number
      experienceYears: number
      isFeatured: boolean
      specialties: string[]
      territories: string[]
      languages: string[]
      office: {
        city: string
        name: string
        address: string
      }
      social: {
        website?: string
        linkedin?: string
        instagram?: string
      }
    }
  }
}

const AgentCard = ({ agent }: AgentCardProps) => {
  return (
    <Card
      className="agent-card"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 4,
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        backgroundColor: '#f9f4f1',
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 12px 40px rgba(0,0,0,0.2)'
        }
      }}
    >
      {/* Photo Section with Overlay */}
      <Box sx={{ position: 'relative', height: 320 }}>
        <CardMedia
          component="img"
          height="320"
          image={agent.data.photo}
          alt={agent.data.fullName}
          sx={{
            objectFit: 'cover',
            width: '100%'
          }}
        />
        
        {/* Dark Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.4)',
            zIndex: 1
          }}
        />

        {/* Name and Title Overlay */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            padding: '40px 20px 20px',
            color: 'white',
            zIndex: 2
          }}
        >
          <Typography
            variant="h4"
            component="h2"
            sx={{
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 800,
              fontSize: { xs: '1.8rem', md: '2rem' },
              textAlign: 'center',
              mb: 1,
              textShadow: '2px 2px 4px rgba(0,0,0,0.7)'
            }}
          >
            {agent.data.fullName}
          </Typography>
          <Typography
            variant="h6"
            component="h3"
            sx={{
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 600,
              fontSize: { xs: '1rem', md: '1.1rem' },
              textAlign: 'center',
              color: '#b08b4f',
              textShadow: '1px 1px 2px rgba(0,0,0,0.7)'
            }}
          >
            REALTOR
          </Typography>
        </Box>
      </Box>

      {/* Content Section - Dark Blue Background */}
      <CardContent
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          padding: '24px',
          backgroundColor: '#0e2340',
          color: 'white'
        }}
      >
        {/* Bio */}
        <Typography
          variant="body2"
          sx={{
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: 400,
            color: 'white',
            fontSize: '0.95rem',
            lineHeight: 1.6,
            mb: 3,
            textAlign: 'center'
          }}
        >
          {agent.data.bio}
        </Typography>

        {/* Contact Information */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Phone sx={{ color: '#b08b4f', fontSize: 18 }} />
            <Typography
              sx={{
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 400,
                color: 'white',
                fontSize: '0.9rem'
              }}
            >
              {agent.data.phone}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Email sx={{ color: '#b08b4f', fontSize: 18 }} />
            <Typography
              sx={{
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 400,
                color: 'white',
                fontSize: '0.9rem'
              }}
            >
              {agent.data.email}
            </Typography>
          </Box>
        </Box>
      </CardContent>

      {/* Learn More Button */}
      <Box sx={{ padding: '0 24px 24px', backgroundColor: '#0e2340' }}>
        <Button
          fullWidth
          variant="contained"
          sx={{
            backgroundColor: '#b08b4f',
            color: 'white',
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: 600,
            fontSize: '0.9rem',
            padding: '14px 24px',
            borderRadius: 2,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            '&:hover': {
              backgroundColor: '#9a7442',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(176, 139, 79, 0.3)'
            },
            transition: 'all 0.2s ease-in-out'
          }}
        >
          Learn More
        </Button>
      </Box>
    </Card>
  )
}

export interface FeaturedAgentsCarouselProps {
  title?: string
  embedId: string
}

const FeaturedAgentsCarousel = ({
  title = "Meet Our Agents",
  embedId
}: FeaturedAgentsCarouselProps) => {
    console.log('FeaturedAgentsCarousel',FeaturedAgentsCarousel)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [activeCardIndex, setActiveCardIndex] = useState(0)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'))
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const cardsTrackRef = useRef<HTMLDivElement>(null)
  
  // Fetch data using the hook
  const { 
    data: contentData, 
    isLoading: isLoadingContent, 
    error: contentError
  } = useContentByEmbed(embedId)
    useEffect(() => {
        console.log('contentData',contentData)
    }, [contentData]);
  
  // Set up scroll listener for mobile - moved before conditional return
  useEffect(() => {
    const container = scrollContainerRef.current
    if (container && isMobile) {
      container.addEventListener('scroll', updateActiveCard)
      // Initial check
      setTimeout(updateActiveCard, 100)
      
      return () => {
        container.removeEventListener('scroll', updateActiveCard)
      }
    }
  }, [isMobile])
  
  // Handle loading state
  if (isLoadingContent) {
    return (
      <Paper 
        elevation={2} 
        sx={{ 
          p: 3, 
          textAlign: 'center',
          backgroundColor: 'grey.100',
          borderRadius: 2
        }}
      >
        <Typography variant="body1" color="text.secondary">
          ...
        </Typography>
      </Paper>
    )
  }

  // Handle error state
  if (contentError) {
    return (
      <Paper 
        elevation={2} 
        sx={{ 
          p: 3, 
          textAlign: 'center',
          backgroundColor: 'grey.100',
          borderRadius: 2
        }}
      >
        <Typography variant="body1" color="error">
          Error al cargar los agentes: {contentError.message}
        </Typography>
      </Paper>
    )
  }

  // Handle no data state
  if (!contentData || !contentData.data || !contentData.data.data || !Array.isArray(contentData.data.data) || contentData.data.data.length === 0) {
    return (
      <Paper 
        elevation={2} 
        sx={{ 
          p: 3, 
          textAlign: 'center',
          backgroundColor: 'grey.100',
          borderRadius: 2
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No hay agentes disponibles
        </Typography>
      </Paper>
    )
  }

  const agents = contentData.data.data
  const itemsPerView = isMobile ? 1 : isTablet ? 2 : 3
  const maxIndex = Math.max(0, agents.length - itemsPerView)

  // Scroll functions for mobile
  const scrollToCard = (index: number) => {
    if (!scrollContainerRef.current || !cardsTrackRef.current) return
    
    const container = scrollContainerRef.current
    const track = cardsTrackRef.current
    const cards = track.querySelectorAll('.agent-card')
    
    if (cards[index]) {
      const card = cards[index] as HTMLElement
      const cardLeft = card.offsetLeft
      const containerWidth = container.offsetWidth
      const cardWidth = card.offsetWidth
      
      // Calculate scroll position to center the card
      const scrollPosition = cardLeft - (containerWidth / 2) + (cardWidth / 2)
      
      container.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      })
    }
  }

  const scrollCards = (direction: 'prev' | 'next') => {
    if (!scrollContainerRef.current || !cardsTrackRef.current) return
    
    const container = scrollContainerRef.current
    const track = cardsTrackRef.current
    const cardWidth = track.querySelector('.agent-card')?.clientWidth || 0
    const gap = 12
    const scrollAmount = cardWidth + gap

    if (direction === 'prev') {
      container.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      })
    } else {
      container.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      })
    }
  }

  // Update active card and scale/fade effects on scroll
  const updateActiveCard = () => {
    if (!scrollContainerRef.current || !cardsTrackRef.current || !isMobile) return
    
    const container = scrollContainerRef.current
    const track = cardsTrackRef.current
    const cards = track.querySelectorAll('.agent-card')
    
    const containerCenter = container.scrollLeft + (container.offsetWidth / 2)
    
    cards.forEach((card, index) => {
      const cardElement = card as HTMLElement
      const cardCenter = cardElement.offsetLeft + (cardElement.offsetWidth / 2)
      const distance = Math.abs(containerCenter - cardCenter)
      
      // Scale and fade cards based on distance from center
      if (distance < cardElement.offsetWidth / 2) {
        cardElement.style.transform = 'scale(1)'
        cardElement.style.opacity = '1'
        setActiveCardIndex(index)
      } else {
        cardElement.style.transform = 'scale(0.88)'
        cardElement.style.opacity = '0.5'
      }
    })
  }

  // Desktop navigation
  const nextSlide = () => {
    if (isMobile) {
      scrollCards('next')
    } else {
      setCurrentIndex(prev => Math.min(prev + 1, maxIndex))
    }
  }

  const prevSlide = () => {
    if (isMobile) {
      scrollCards('prev')
    } else {
      setCurrentIndex(prev => Math.max(prev - 1, 0))
    }
  }

  const visibleAgents = isMobile ? agents : agents.slice(currentIndex, currentIndex + itemsPerView)

  return (
    <WidgetContainer 
      id="featured-agents-widget"
      className="widget-scoped"
      sx={{
        backgroundColor: '#ffffff',
        py: 6,
        px: 2,
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden',
        '& *': {
          fontFamily: "'Montserrat', sans-serif",
        }
      }}
    >
      {/* Google Fonts Import */}
      <link 
        href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800&display=swap" 
        rel="stylesheet" 
      />
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            {/* Top Image */}
            <Box sx={{ mb: 3 }}>
              <img
                src={topImage}
                alt="Featured Agents"
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  maxHeight: '80px'
                }}
              />
            </Box>
            
            <Typography
              variant="h2"
              component="h1"
              sx={{
                color: 'white',
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 800,
                mb: 2,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                letterSpacing: '0.5px'
              }}
            >
              {title}
            </Typography>
          </Box>

          {/* Carousel Container */}
          <Box sx={{ position: 'relative' }}>
            {/* Navigation Arrows - Only show on desktop */}
            {!isMobile && (
              <>
                <IconButton
                  onClick={prevSlide}
                  disabled={currentIndex === 0}
                  sx={{
                    position: 'absolute',
                    left: -6,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'white',
                    color: '#b08b4f',
                    zIndex: 10,
                    width: 42,
                    height: 42,
                    borderRadius: '50%',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    '&:hover': {
                      backgroundColor: '#b08b4f',
                      color: 'white'
                    },
                    '&:disabled': {
                      opacity: 0.5,
                      cursor: 'not-allowed'
                    }
                  }}
                >
                  <ArrowBackIos />
                </IconButton>

                <IconButton
                  onClick={nextSlide}
                  disabled={currentIndex >= maxIndex}
                  sx={{
                    position: 'absolute',
                    right: -6,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'white',
                    color: '#b08b4f',
                    zIndex: 10,
                    width: 42,
                    height: 42,
                    borderRadius: '50%',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    '&:hover': {
                      backgroundColor: '#b08b4f',
                      color: 'white'
                    },
                    '&:disabled': {
                      opacity: 0.5,
                      cursor: 'not-allowed'
                    }
                  }}
                >
                  <ArrowForwardIos />
                </IconButton>
              </>
            )}

            {/* Agents Container */}
            {isMobile ? (
              // Mobile: Horizontal scroll container
              <Box
                ref={scrollContainerRef}
                sx={{
                  overflowX: 'scroll',
                  overflowY: 'visible',
                  scrollSnapType: 'x mandatory',
                  WebkitOverflowScrolling: 'touch',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  padding: '20px 0',
                  margin: 0,
                  '&::-webkit-scrollbar': {
                    display: 'none'
                  }
                }}
              >
                <Box
                  ref={cardsTrackRef}
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    paddingLeft: '10vw',
                    paddingRight: '10vw',
                    paddingTop: '20px',
                    paddingBottom: '20px',
                    '&::after': {
                      content: '""',
                      flex: '0 0 1px',
                      width: '1px'
                    }
                  }}
                >
                  {agents.map((agent: any, index: number) => (
                    <Box
                      key={agent.id}
                      className="agent-card"
                      sx={{
                        minWidth: '80vw',
                        maxWidth: '80vw',
                        flex: '0 0 80vw',
                        scrollSnapAlign: 'center',
                        scrollSnapStop: 'always',
                        transition: 'transform 0.4s ease, opacity 0.4s ease',
                        transform: index === 0 ? 'scale(1)' : 'scale(0.88)',
                        opacity: index === 0 ? 1 : 0.5,
                        marginRight: index === agents.length - 1 ? '10vw' : 0
                      }}
                    >
                      <AgentCard agent={agent} />
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : (
              // Desktop: Grid layout
              <Box 
                sx={{ 
                  display: 'grid',
                  gridTemplateColumns: isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                  gap: 3,
                  px: 2
                }}
              >
                {visibleAgents.map((agent: any) => (
                  <Box key={agent.id}>
                    <AgentCard agent={agent} />
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* Dots Indicator - Only show on mobile */}
          {isMobile && (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.25, mt: 2.5 }}>
              {agents.map((_: any, index: number) => (
                <Box
                  key={index}
                  onClick={() => scrollToCard(index)}
                  sx={{
                    width: index === activeCardIndex ? 12 : 10,
                    height: index === activeCardIndex ? 12 : 10,
                    borderRadius: '50%',
                    backgroundColor: index === activeCardIndex ? '#b08b4f' : 'rgba(255,255,255,0.3)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: index === activeCardIndex ? '#b08b4f' : 'rgba(255,255,255,0.5)'
                    }
                  }}
                />
              ))}
            </Box>
          )}

          {/* Desktop Dots Indicator */}
          {!isMobile && (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 4 }}>
              {Array.from({ length: maxIndex + 1 }, (_, index) => (
                <Box
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: index === currentIndex ? '#b08b4f' : 'rgba(255,255,255,0.3)',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s ease',
                    '&:hover': {
                      backgroundColor: index === currentIndex ? '#b08b4f' : 'rgba(255,255,255,0.5)'
                    }
                  }}
                />
              ))}
            </Box>
          )}

          {/* View All Button */}
          <Box sx={{ textAlign: 'center', mt: { xs: 4, md: 6 } }}>
            <Button
              variant="contained"
              size="large"
              sx={{
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 700,
                backgroundColor: '#b08b4f',
                color: 'white',
                px: { xs: 4, md: 6 },
                py: { xs: 1.5, md: 2 },
                fontSize: { xs: '1rem', md: '1.1rem' },
                borderRadius: 2,
                textTransform: 'none',
                boxShadow: '0 4px 15px rgba(176, 139, 79, 0.4)',
                '&:hover': {
                  backgroundColor: 'white',
                  color: '#b08b4f',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(176, 139, 79, 0.5)'
                }
              }}
            >
              View All Agents
            </Button>
          </Box>
        </Container>
    </WidgetContainer>
  )
}

export default FeaturedAgentsCarousel
